import { useEffect, useState, useRef } from "react";
import { Eye, Download, RefreshCw, Trash2 } from "lucide-react";
import { Page } from "../../components/Page";
import CustomButton from "../../components/Button";
import Spinner from "../../components/Spinners";
import { PopupModal } from "../../components/Modal";
import { useDocuments } from "../../helpers/hooks/useDocuments";
import { Document } from "../../types/documents";
import { DocumentStatusBadge } from "./components/DocumentStatusBadge";
import { DistractorBadge } from "./components/DistractorBadge";
import { ViewChunksModal } from "./components/ViewChunksModal";
import { DocumentContentModal } from "./components/DocumentContentModal";
import { Dropzone } from "./components/Dropzone";
import { UploadOverlay } from "./components/UploadOverlay";
import { ExampleStructureModal } from "./components/ExampleStructureModal";
import { getUser } from "../../helpers/utils";
import moment from "moment";

export function Documents() {
  const {
    documents,
    isLoading,
    error,
    fetchDocuments,
    regenerateDocument,
    downloadDocument,
    deleteDocument,
    uploadFiles,
    pendingUploads,
    updateDocument,
  } = useDocuments();

  const [showChunksModal, setShowChunksModal] = useState<{
    documentId: string;
    documentName: string;
  } | null>(null);
  const [showContentModal, setShowContentModal] = useState<{
    documentId: string;
    documentName: string;
  } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState<
    string | null
  >(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isDraggingOverPage, setIsDraggingOverPage] = useState(false);
  const [showDistractors, setShowDistractors] = useState(false);
  const [showExampleModal, setShowExampleModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pageDragCounter = useRef(0);
  const user = getUser();

  const distractorCount = documents.filter((d) => d.isDistractor).length;
  const visibleDocuments = showDistractors
    ? documents
    : documents.filter((d) => !d.isDistractor);

  const handleFilesDropped = (files: File[]) => {
    if (!user?.id) return;
    uploadFiles(user.id, files);
  };

  const handlePageDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    pageDragCounter.current += 1;
    if (pageDragCounter.current === 1) setIsDraggingOverPage(true);
  };

  const handlePageDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    pageDragCounter.current = Math.max(0, pageDragCounter.current - 1);
    if (pageDragCounter.current === 0) setIsDraggingOverPage(false);
  };

  const handlePageDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handlePageDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    pageDragCounter.current = 0;
    setIsDraggingOverPage(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) handleFilesDropped(files);
  };

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const openChunksFor = (doc: Document) => {
    if (doc.status !== "embedded") return;
    setShowChunksModal({ documentId: doc.id, documentName: doc.name });
  };

  const openContentFor = (doc: Document) => {
    setShowContentModal({ documentId: doc.id, documentName: doc.name });
  };

  const handleRegenerate = async (documentId: string) => {
    setIsRegenerating(true);
    try {
      await regenerateDocument(documentId);
      setShowRegenerateConfirm(null);
    } catch (err) {
      // Error is handled in the hook
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDelete = async (documentId: string) => {
    try {
      await deleteDocument(documentId);
      setShowDeleteConfirm(null);
    } catch (err) {
      // Error is handled in the hook
    }
  };

  const formatRelativeTime = (date: Date | null): string => {
    if (!date) return "-";
    return moment(date).fromNow();
  };

  const getErrorDetails = (doc: Document): string | undefined => {
    if (doc.status === "error" && doc.chunkStrategy?.startsWith("error:")) {
      return doc.chunkStrategy.replace("error:", "").trim();
    }
    return undefined;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0 || !user?.id) return;
    uploadFiles(user.id, files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Empty state (true empty: no docs and nothing uploading)
  if (
    !isLoading &&
    documents.length === 0 &&
    pendingUploads.length === 0 &&
    !error
  ) {
    return (
      <Page>
        <div className="py-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-surface-tertiary flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept=".md,.markdown,text/markdown"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <div className="flex flex-col items-center justify-center py-12 px-4">
            {/* Warning Banner */}
            <div className="w-full max-w-2xl mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex gap-3">
                <span className="text-2xl flex-shrink-0">⚠️</span>
                <div className="flex flex-col gap-2">
                  <h4 className="text-content-primary text-sm font-semibold">
                    System-Wide Documents
                  </h4>
                  <p className="text-content-secondary text-xs leading-relaxed">
                    Documents uploaded here are accessible by AI across <strong>all hotels and users</strong>. Only upload generic, non-sensitive content such as user FAQs, platform guides, or public documentation. Do not upload hotel-specific data, guest information, pricing strategies, or any confidential information.
                  </p>
                </div>
              </div>
            </div>

            <Dropzone
              onClick={() => fileInputRef.current?.click()}
              onFilesDropped={handleFilesDropped}
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-surface-secondary rounded-full flex items-center justify-center mb-4">
                  <span className="text-4xl">📄</span>
                </div>
                <h3 className="text-content-primary text-xl font-medium mb-2">
                  No documents uploaded yet
                </h3>
                <p className="text-content-secondary text-sm mb-2 max-w-md">
                  Drag &amp; drop Markdown (.md) files here, or click to upload.
                </p>
                <div onClick={(e) => e.stopPropagation()} className="mb-6">
                  <button
                    type="button"
                    onClick={() => setShowExampleModal(true)}
                    className="text-sm text-brand-primary hover:underline font-medium"
                  >
                    See example format
                  </button>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <CustomButton
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload Document
                  </CustomButton>
                </div>
              </div>
            </Dropzone>
          </div>
        </div>
        {showExampleModal && (
          <ExampleStructureModal onClose={() => setShowExampleModal(false)} />
        )}
      </Page>
    );
  }

  return (
    <Page>
      <div
        className="relative py-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1"
        onDragEnter={handlePageDragEnter}
        onDragLeave={handlePageDragLeave}
        onDragOver={handlePageDragOver}
        onDrop={handlePageDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".md,.markdown,text/markdown"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Header */}
        <div className="px-4 flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-2xl font-semibold text-content-primary">AI Document Library</h1>
            <div className="text-sm text-content-primary">
              Manage Markdown FAQ documents used by the AI chatbot across all hotels
            </div>
          </div>
          <div className="flex items-center gap-4">
            {distractorCount > 0 && (
              <label className="flex items-center gap-2 text-xs text-content-secondary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showDistractors}
                  onChange={(e) => setShowDistractors(e.target.checked)}
                  className="accent-amber-500"
                />
                <span>
                  Show distractors{" "}
                  <span className="text-content-tertiary">
                    ({distractorCount} hidden{showDistractors ? ", now visible" : ""})
                  </span>
                </span>
              </label>
            )}
            <button
              type="button"
              onClick={() => setShowExampleModal(true)}
              className="text-sm text-brand-primary hover:underline font-medium"
            >
              See example format
            </button>
            <CustomButton
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Document
            </CustomButton>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="px-4 mb-6">
          <div className="w-full p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex gap-3">
              <span className="text-2xl flex-shrink-0">⚠️</span>
              <div className="flex flex-col gap-2">
                <h4 className="text-content-primary text-sm font-semibold">
                  System-Wide Documents
                </h4>
                <p className="text-content-secondary text-xs leading-relaxed">
                  Documents uploaded here are accessible by AI across <strong>all hotels and users</strong>. Only upload generic, non-sensitive content such as user FAQs, platform guides, or public documentation. Do not upload hotel-specific data, guest information, pricing strategies, or any confidential information.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="!text-brand-primary" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4 px-4">
            <span className="text-red-600 text-sm">{error}</span>
            <CustomButton onClick={fetchDocuments}>
              Retry
            </CustomButton>
          </div>
        ) : (
          <div className="px-4">
            {/* Desktop Table View */}
            <div className="hidden lg:block w-full overflow-x-auto border border-border-secondary rounded-lg bg-surface-primary">
              <table className="w-full">
                <thead className="bg-surface-secondary">
                  <tr>
                    <th className="text-left text-content-secondary text-sm font-medium py-3 px-4">
                      Name
                    </th>
                    <th className="text-left text-content-secondary text-sm font-medium py-3 px-4">
                      Status
                    </th>
                    <th className="text-left text-content-secondary text-sm font-medium py-3 px-4">
                      Chunks
                    </th>
                    <th className="text-left text-content-secondary text-sm font-medium py-3 px-4">
                      Last Embedded
                    </th>
                    <th className="text-left text-content-secondary text-sm font-medium py-3 px-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUploads.map((p) => (
                    <tr
                      key={p.tempId}
                      className="border-t border-border-secondary bg-surface-tertiary/40"
                    >
                      <td className="py-3 px-4 text-sm text-content-secondary italic">
                        {p.name}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-primary/10 text-brand-primary">
                          <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                            <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A8.009 8.009 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Uploading…
                        </span>
                      </td>
                      <td className="py-3 px-4 text-content-secondary text-sm">—</td>
                      <td className="py-3 px-4 text-content-secondary text-sm">—</td>
                      <td className="py-3 px-4" />
                    </tr>
                  ))}
                  {visibleDocuments.map((doc) => {
                    const canViewChunks = doc.status === "embedded";
                    const rowClass = doc.isDistractor
                      ? "border-t border-border-secondary bg-amber-50/40 hover:bg-amber-50 transition-colors"
                      : "border-t border-border-secondary hover:bg-surface-hover transition-colors";
                    return (
                      <tr key={doc.id} className={rowClass}>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => openContentFor(doc)}
                              title="View and edit content"
                              className="text-left text-content-primary hover:text-brand-primary hover:underline"
                            >
                              {doc.name}
                            </button>
                            {doc.isDistractor && <DistractorBadge />}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <DocumentStatusBadge
                            status={doc.status}
                            errorDetails={getErrorDetails(doc)}
                          />
                        </td>
                        <td className="py-3 px-4 text-content-secondary text-sm">
                          {doc.chunkCount || "-"}
                        </td>
                        <td className="py-3 px-4 text-content-secondary text-sm">
                          {formatRelativeTime(doc.lastEmbeddedAt)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openChunksFor(doc)}
                              disabled={!canViewChunks}
                              title="View chunks"
                              aria-label="View chunks"
                              className="p-2 rounded text-content-secondary hover:text-brand-primary hover:bg-surface-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-content-secondary disabled:hover:bg-transparent"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => downloadDocument(doc.id)}
                              title="Download"
                              aria-label="Download"
                              className="p-2 rounded text-content-secondary hover:text-brand-primary hover:bg-surface-hover transition-colors"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowRegenerateConfirm(doc.id)}
                              title="Regenerate embeddings"
                              aria-label="Regenerate embeddings"
                              className="p-2 rounded text-content-secondary hover:text-brand-primary hover:bg-surface-hover transition-colors"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowDeleteConfirm(doc.id)}
                              title="Delete"
                              aria-label="Delete"
                              className="p-2 rounded text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3">
              {pendingUploads.map((p) => (
                <div
                  key={`pending-${p.tempId}`}
                  className="border border-border-secondary rounded-lg p-4 bg-surface-tertiary/40"
                >
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-sm font-medium text-content-secondary flex-1 italic">
                      {p.name}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-primary/10 text-brand-primary">
                      <Spinner className="!text-brand-primary w-3 h-3" />
                      Uploading…
                    </span>
                  </div>
                </div>
              ))}
              {visibleDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className={`border rounded-lg p-4 ${
                    doc.isDistractor
                      ? "border-amber-300 bg-amber-50/40"
                      : "border-border-secondary bg-surface-primary"
                  }`}
                >
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <button
                      type="button"
                      onClick={() => openContentFor(doc)}
                      className="text-left text-sm font-medium text-content-primary flex-1 hover:text-brand-primary hover:underline"
                    >
                      {doc.name}
                    </button>
                    <DocumentStatusBadge
                      status={doc.status}
                      errorDetails={getErrorDetails(doc)}
                    />
                  </div>
                  {doc.isDistractor && (
                    <div className="mb-3">
                      <DistractorBadge />
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-xs text-content-secondary mb-3">
                    <span>{doc.chunkCount || "-"} chunks</span>
                    <span>•</span>
                    <span>{formatRelativeTime(doc.lastEmbeddedAt)}</span>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() =>
                        setShowChunksModal({
                          documentId: doc.id,
                          documentName: doc.name,
                        })
                      }
                      disabled={doc.status !== "embedded"}
                      className="px-3 py-1.5 text-xs font-medium text-content-primary bg-surface-secondary border border-border-secondary rounded-lg hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      View Chunks
                    </button>
                    <button
                      onClick={() => downloadDocument(doc.id)}
                      className="px-3 py-1.5 text-xs font-medium text-content-primary bg-surface-secondary border border-border-secondary rounded-lg hover:bg-surface-hover transition-colors"
                    >
                      Download
                    </button>
                    <button
                      onClick={() => setShowRegenerateConfirm(doc.id)}
                      className="px-3 py-1.5 text-xs font-medium text-content-primary bg-surface-secondary border border-border-secondary rounded-lg hover:bg-surface-hover transition-colors"
                    >
                      Regenerate
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(doc.id)}
                      className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <UploadOverlay visible={isDraggingOverPage} />
      </div>

      {/* Modals */}
      {showExampleModal && (
        <ExampleStructureModal onClose={() => setShowExampleModal(false)} />
      )}

      {showContentModal && (
        <DocumentContentModal
          documentId={showContentModal.documentId}
          initialName={showContentModal.documentName}
          onClose={() => setShowContentModal(null)}
          onSaved={updateDocument}
        />
      )}

      {showChunksModal && (
        <ViewChunksModal
          documentId={showChunksModal.documentId}
          documentName={showChunksModal.documentName}
          onClose={() => setShowChunksModal(null)}
        />
      )}

      {showRegenerateConfirm && (
        <PopupModal
          onClose={() => setShowRegenerateConfirm(null)}
          contentClassName="max-w-md w-full mx-auto"
        >
          <div className="flex flex-col gap-4">
            <h3 className="text-black text-xl font-semibold">
              Regenerate Embeddings?
            </h3>
            <p className="text-gray-600 text-sm">
              This will delete existing embeddings and recreate them. The
              process may take 10-15 seconds.
            </p>
            <div className="flex gap-3 justify-end">
              <CustomButton
                onClick={() => setShowRegenerateConfirm(null)}
                disabled={isRegenerating}
              >
                Cancel
              </CustomButton>
              <CustomButton
                onClick={() => handleRegenerate(showRegenerateConfirm)}
                disabled={isRegenerating}
              >
                {isRegenerating ? <Spinner /> : "Regenerate"}
              </CustomButton>
            </div>
          </div>
        </PopupModal>
      )}

      {showDeleteConfirm && (
        <PopupModal
          onClose={() => setShowDeleteConfirm(null)}
          contentClassName="max-w-md w-full mx-auto"
        >
          <div className="flex flex-col gap-4">
            <h3 className="text-black text-xl font-semibold">
              Delete Document?
            </h3>
            <p className="text-gray-600 text-sm">
              This will remove the document and all embeddings. The AI will no
              longer be able to reference this document.
            </p>
            <div className="flex gap-3 justify-end">
              <CustomButton
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </CustomButton>
              <CustomButton onClick={() => handleDelete(showDeleteConfirm)}>
                Delete
              </CustomButton>
            </div>
          </div>
        </PopupModal>
      )}
    </Page>
  );
}
